import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import { Controls } from './Common'

import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import NoAirportsOption from 'components/Flights/FlightsSearch/SearchWidget/FlightAirportSelect/NoAirportsOption'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import ListItem from 'components/Luxkit/List/ListItem'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import TextLink from 'components/Luxkit/TextLink'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import useAirportTypeahead from 'hooks/GlobalSearch/useAirportTypeahead'
import { useTripAirports } from 'tripPlanner/hooks/api/flights'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'

const RESULT_MAX_COUNT = 15
const CHAR_THRESHOLD = 1

interface Props {
  airportType: 'departure' | 'arrival'
  tripId: string
  flightId: string
}

const Results = styled.div`
  & > * {
    width: 100%;
  }
`

function SearchAirports(props: Props) {
  const { airportType, tripId, flightId } = props
  const destinationAirports = useTripAirports({ tripId })

  const flightSearchDispatch = useContext(FlightSearchWidgetDispatchContext)

  const inputRef = useRef<HTMLInputElement>(null)
  const { closeModal, goBack } = useModalCallbacks()

  const [searchTerm, setSearchTerm] = useState<string>('')

  const { results, isLoading } = useAirportTypeahead(searchTerm, {
    maxResults: RESULT_MAX_COUNT,
    characterThreshold: CHAR_THRESHOLD,
  })

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.currentTarget.value)
    },
    [setSearchTerm],
  )

  const onClear = () => setSearchTerm('')

  const onSelectAirport = useCallback(
    (airportItem: App.AirportLocation) => {
      if (airportType === 'arrival') {
        flightSearchDispatch({
          type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT,
          airport: airportItem,
          id: flightId,
        })
      }
      if (airportType === 'departure') {
        flightSearchDispatch({
          type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT,
          airport: airportItem,
          id: flightId,
        })
      }

      goBack?.()
    },
    [flightSearchDispatch, airportType, goBack, flightId],
  )

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <>
      <ModalHeader
        title="Search airports"
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      >
        <Controls>
          <TextInput
            ref={inputRef}
            placeholder="Search by airport"
            value={searchTerm}
            onChange={onInputChange}
            noValidationMessage
            noValidationSpacing
          />
          {searchTerm.length > 1 && (
            <TextLink
              style={{ width: 'initial' }}
              variant="default"
              onClick={onClear}
            >
              Clear
            </TextLink>
          )}
        </Controls>
      </ModalHeader>
      <ModalBody>
        <ModalContent>
          <Results>
            {isLoading && <LoadingIndicator inline />}
            {!isLoading &&
              searchTerm.length <= CHAR_THRESHOLD &&
              results.length === 0 &&
              destinationAirports.map((airport) => (
                <ListItem
                  key={airport.airportCode}
                  startIcon={<LinePlaneFlyIcon />}
                  title={`${airport.airportName} (${
                    airport.isMultiAirport ?
                      airport.cityCode :
                      airport.airportCode
                  })`}
                  subtitle={`${airport.cityDesc} - ${airport.country}`}
                  onClick={() => onSelectAirport(airport)}
                />
              ))}
            {!isLoading &&
              results.map((airport) => (
                <ListItem
                  key={airport.airportCode}
                  startIcon={<LinePlaneFlyIcon />}
                  title={`${airport.airportName} (${
                    airport.isMultiAirport ?
                      airport.cityCode :
                      airport.airportCode
                  })`}
                  subtitle={`${airport.cityDesc} - ${airport.country}`}
                  onClick={() => onSelectAirport(airport)}
                />
              ))}
            {!isLoading &&
              results.length === 0 &&
              searchTerm.length > CHAR_THRESHOLD && (
                <NoAirportsOption inputValue={searchTerm} />
            )}
          </Results>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SearchAirports
